/* eslint-disable no-param-reassign */
import { DatePicker } from 'antd';
import { RangePickerBaseProps } from 'antd/lib/date-picker/generatePicker';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';

type FormatDate = 'DD/MM/YYYY' | 'MM/YYYY' | 'DD/MM';
type FormatType = 'fullDate' | 'monthYear' | 'dayMonth';
// Omit<RangePickerProps, 'format'>
type Props = Omit<RangePickerBaseProps<Moment>, 'picker' | 'format'> & {
  format?: FormatDate;
};
type RangeDate = [Moment | null, Moment | null];
type RangePickerDate = RangeDate | null;

const numberRule = /\d+/g;
const formatDefault = 'DD/MM/YYYY';
const formatTypes: Record<FormatDate, FormatType> = {
  'DD/MM/YYYY': 'fullDate',
  'MM/YYYY': 'monthYear',
  'DD/MM': 'dayMonth',
};

const RangePicker: React.FC<Props> = ({ format = formatDefault, ...props }) => {
  const hash = window.btoa(encodeURIComponent(moment().unix()));
  const newClassName = `range-picker-${(props.id || props.className) ?? hash}`;
  const [value, setValue] = useState<RangePickerDate>(null);

  useEffect(() => {
    const rangePickerElement = document.getElementsByClassName(newClassName);
    const inputsElement =
      rangePickerElement[0]?.querySelectorAll('input') ?? [];

    if (inputsElement.length) {
      if (inputsElement[0]) {
        configInput(inputsElement[0], 0);
      }

      if (inputsElement[1]) {
        configInput(inputsElement[1], 1);
      }
    }

    return () => {
      if (inputsElement.length) {
        if (inputsElement[0]) {
          desconfigInput(inputsElement[0], 0);
        }

        if (inputsElement[1]) {
          desconfigInput(inputsElement[1], 1);
        }
      }
    };
  }, []);

  useEffect(() => {
    setValue(props.value ?? null);
  }, [props?.value]);

  function configInput(inputElement: HTMLInputElement, index: number) {
    inputElement.maxLength = format.length;
    inputElement.oninput = onInput;
    inputElement.addEventListener('keydown', () =>
      handleKeydownInput(inputElement),
    );
    inputElement.addEventListener('keyup', () =>
      handleKeyupInput(inputElement, index),
    );
  }

  function desconfigInput(inputElement: HTMLInputElement, index: number) {
    inputElement.removeEventListener('keydown', () =>
      handleKeydownInput(inputElement),
    );
    inputElement.removeEventListener('keyup', () =>
      handleKeyupInput(inputElement, index),
    );
  }

  function setInputValue(inputValue: string, input: HTMLInputElement) {
    if (input) {
      input.value = inputValue;
      input.title = inputValue;
    }
  }

  function onInput(this: GlobalEventHandlers) {
    const thisInput = this as HTMLInputElement;

    thisInput.value = thisInput.value.match(numberRule)?.join('/') ?? '';
  }

  function handleKeydownInput(input: HTMLInputElement) {
    if (!input) return null;

    let nextValue = input.value;

    if (nextValue.length === 3) {
      nextValue = `${nextValue.slice(0, 2)}/${nextValue.slice(2)}`;
    } else if (nextValue.length === 6 && formatTypes[format] === 'monthYear') {
      nextValue = `${nextValue.slice(0, 7)}`;
    } else if (nextValue.length === 6 && formatTypes[format] === 'fullDate') {
      nextValue = `${nextValue.slice(0, 5)}/${nextValue.slice(5)}`;
    }

    setInputValue(nextValue, input);

    return null;
  }

  const handleKeyupInput = (input: HTMLInputElement, index: number) => {
    if (!input) return null;

    if (input.value.length === format.length) {
      const date = moment(input.value, format, true);
      let nextValue = value;

      if (!nextValue) {
        nextValue = !index ? [date, null] : [null, date];
      } else {
        nextValue[index] = date;
      }

      nextValue = handleSetValue(nextValue);

      if (!nextValue) return null;

      if (
        nextValue[0] &&
        nextValue[0].isValid() &&
        nextValue[1] &&
        nextValue[1].isValid()
      ) {
        handleChange(nextValue, [format, format]);
      }
    }

    return null;
  };

  function handleSetValue(nextValue: RangePickerDate) {
    let newNextValue = nextValue;

    setValue((prevValue) => {
      if (!prevValue) return nextValue;

      newNextValue = prevValue.map((prevDate, prevIndex) =>
        nextValue && nextValue[prevIndex] ? nextValue[prevIndex] : prevDate,
      ) as RangeDate;

      return newNextValue;
    });

    return newNextValue;
  }

  function handleChange(date: RangePickerDate, dateString: [string, string]) {
    handleSetValue(date);

    if (props.onChange) {
      props.onChange(date, dateString);
    }
  }

  return (
    <DatePicker.RangePicker
      {...props}
      className={newClassName}
      format={format}
      onChange={handleChange}
      value={value}
    />
  );
};

export default RangePicker;
